/**
 * Vendor staff access control. A store owner (the `vendor` account created with the store)
 * groups their staff into named roles ("Manager", "Staff", …) and gives each group an access
 * level per vendor-portal screen. Staff accounts carry the same `vendor` role + `storeId` claim
 * as the owner — so every existing store-scoping check applies to them unchanged — plus a
 * `staffGroupId` claim that narrows them to their group's permissions.
 */
export const STAFF_MODULES = {
  ORDERS: 'orders',
  CUSTOMERS: 'customers',
  PRODUCTS: 'products',
  TAXONOMY: 'taxonomy',
  HOMEPAGE: 'homepage',
  NAVIGATION: 'navigation',
  COUPONS: 'coupons',
  DISCOUNTS: 'discounts',
  DELIVERY_ZONES: 'delivery_zones',
  REVIEWS: 'reviews',
  SETTINGS: 'settings',
} as const;

export type StaffModuleValue = (typeof STAFF_MODULES)[keyof typeof STAFF_MODULES];

export const STAFF_ACCESS = {
  NONE: 'none',
  VIEW: 'view',
  EDIT: 'edit',
} as const;

export type StaffAccessValue = (typeof STAFF_ACCESS)[keyof typeof STAFF_ACCESS];

export type StaffPermissions = Record<StaffModuleValue, StaffAccessValue>;

/** Display order + labels for the permission matrix in the vendor portal. */
export const STAFF_MODULE_LABELS: Readonly<Record<StaffModuleValue, string>> = {
  [STAFF_MODULES.ORDERS]: 'Orders',
  [STAFF_MODULES.CUSTOMERS]: 'Customers',
  [STAFF_MODULES.PRODUCTS]: 'Products',
  [STAFF_MODULES.TAXONOMY]: 'Taxonomy',
  [STAFF_MODULES.HOMEPAGE]: 'Homepage Builder',
  [STAFF_MODULES.NAVIGATION]: 'Navigation',
  [STAFF_MODULES.COUPONS]: 'Coupons',
  [STAFF_MODULES.DISCOUNTS]: 'Discounts',
  [STAFF_MODULES.DELIVERY_ZONES]: 'Delivery Zones',
  [STAFF_MODULES.REVIEWS]: 'Reviews',
  [STAFF_MODULES.SETTINGS]: 'Store Settings (contact, email, branding, pages)',
};

export function hasStaffAccess(
  permissions: StaffPermissions | null | undefined,
  module: StaffModuleValue,
  needed: Exclude<StaffAccessValue, 'none'>,
): boolean {
  // null = not a staff member (store owner, platform staff) — unrestricted by this layer.
  if (!permissions) return true;
  const level = permissions[module] ?? STAFF_ACCESS.NONE;
  return needed === STAFF_ACCESS.VIEW ? level !== STAFF_ACCESS.NONE : level === STAFF_ACCESS.EDIT;
}
